// src/hooks/useConsultation.js
//
// All state and side effects of a consultation, so the components under
// components/doctor/ can stay presentational: opening / starting the visit,
// the debounced autosave of the draft, the patient context (vitals, allergies,
// reports, open items, prescriptions, history), private notes, and the
// sign-and-complete sequence.
//
// Visit data lives in the Visit (PUT /doctor/visits/{id}, autosaved while DRAFT);
// private notes stay in doctor_notes via doctorNoteAPI and are never part of the
// visit. Signing runs prescriptions first, then the visit
// (utils/consultation.js → signConsultation); after that the page is read-only.

import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { doctorNoteAPI, doctorActionItemAPI, triageReportAPI } from '../services/doctorApi';
import {
  ensureVisit, updateVisit, signVisit, startConsultation, getPatientVitals, getPatientSnapshot, listPatientVisits,
  getClinicQueue,
} from '../services/visitApi';
import { listPatientPrescriptions, signPrescription } from '../services/prescriptionApi';
import {
  buildDraftPayload, createDebouncer, describeSignFailure, draftKey, formFromVisit, isDraftDirty, signConsultation,
} from '../utils/consultation';
import { canSign } from '../utils/consultSteps';
import { describeItem } from '../utils/prescription';
import { copyMedicinesMessage } from '../utils/copyMedicines';

export const AUTOSAVE_MS = 1000;
export const AUTO_START = ['SCHEDULED', 'CONFIRMED', 'IN_CONSULTATION', 'PENDING'];

const unwrap = res => res?.data?.content || res?.data || [];
export const fmtDate = v => (v
  ? new Date(v).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—');

// "No medicines needed" is a UI decision, not part of the Visit contract; it is
// remembered per visit for this browser session so a refresh keeps the step complete.
const noMedsKey = id => `previu.consult.noMeds.${id}`;
const readNoMeds = id => { try { return sessionStorage.getItem(noMedsKey(id)) === '1'; } catch { return false; } };
const writeNoMeds = (id, on) => {
  try { if (on) sessionStorage.setItem(noMedsKey(id), '1'); else sessionStorage.removeItem(noMedsKey(id)); } catch { /* storage unavailable */ }
};

export default function useConsultation({ appointment, patientId, onStatusChange }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const status = appointment.status;
  const appointmentId = appointment.id;

  const [phase, setPhase] = useState(AUTO_START.includes(status) ? 'loading' : 'idle'); // idle|loading|ready|error
  const [loadError, setLoadError] = useState(null);
  const [visit, setVisit] = useState(null);
  const [form, setForm] = useState(() => formFromVisit(null));
  const [saveState, setSaveState] = useState('saved');
  const [savedAt, setSavedAt] = useState(null);

  // Patient context
  const [vitals, setVitals] = useState(undefined);       // undefined = loading, null = none / unavailable
  const [patient, setPatient] = useState({ age: null, gender: null });
  const [allergies, setAllergies] = useState(undefined);  // undefined = loading, null = not available, '' = none recorded
  const [allRx, setAllRx] = useState(null);              // null = loading
  const [notes, setNotes] = useState([]);
  const [queueEntry, setQueueEntry] = useState(null);     // token + consultation start, from GET /clinic/queue
  const [context, setContext] = useState({ status: 'loading', reports: [], items: [] });
  const [history, setHistory] = useState({ status: 'idle', visits: [] }); // idle|loading|ready|error

  // Prescription step
  const [rxItems, setRxItems] = useState([]);
  const [noMedicines, setNoMedicinesState] = useState(false);
  const builderRef = useRef(null);

  // Notes + sign
  const [noteDraft, setNoteDraft] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signProblem, setSignProblem] = useState(null);

  const locked = visit?.status === 'SIGNED';
  const visitId = visit?.id;
  const visitTag = `Visit ${fmtDate(appointment.appointmentDate)}`;
  const formRef = useRef(form);
  formRef.current = form;
  const savedKey = useRef(null);

  // ── Load / ensure the visit ───────────────────────────────────────────────
  const openVisit = useCallback(async ({ start = false } = {}) => {
    setPhase('loading'); setLoadError(null);
    try {
      if (start) {
        await startConsultation(appointmentId);
        onStatusChange?.('IN_CONSULTATION');
      }
      const v = await ensureVisit(appointmentId);
      const f = formFromVisit(v);
      savedKey.current = draftKey(f);
      setVisit(v); setForm(f); setSaveState('saved'); setPhase('ready');
      setNoMedicinesState(readNoMeds(v.id));
    } catch (err) {
      setLoadError(err?.response?.status === 403
        ? 'This appointment belongs to another doctor.'
        : err?.response?.status === 409
          ? 'This patient is no longer waiting to be called in.'
          : "Couldn't open the consultation.");
      setPhase('error');
    }
  }, [appointmentId, onStatusChange]);

  useEffect(() => {
    if (AUTO_START.includes(status) && !visit && phase === 'loading') openVisit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Context once we have a visit ──────────────────────────────────────────
  const loadRx = useCallback(async () => {
    try { setAllRx(await listPatientPrescriptions(patientId)); } catch { setAllRx([]); }
  }, [patientId]);

  useEffect(() => {
    if (!visitId || !patientId) return undefined;
    let live = true;
    loadRx();
    getPatientVitals(patientId, 1).then(r => live && setVitals(r[0] || null)).catch(() => live && setVitals(null));
    getPatientSnapshot(patientId, user?.email)
      .then(s => { if (live) { setAllergies(s.allergies); setPatient({ age: s.age, gender: s.gender }); } })
      .catch(() => live && setAllergies(null));
    doctorNoteAPI.listByPatient(patientId).then(res => live && setNotes(unwrap(res))).catch(() => {});
    getClinicQueue({ date: appointment.appointmentDate })
      .then(q => live && setQueueEntry(q.find(e => e.appointmentId === appointmentId) || null)).catch(() => {});
    (async () => {
      const [tri, act] = await Promise.allSettled([
        triageReportAPI.listByPatient(patientId), doctorActionItemAPI.listByPatient(patientId),
      ]);
      if (!live) return;
      setContext({
        status: tri.status === 'rejected' && act.status === 'rejected' ? 'error' : 'ready',
        reports: tri.status === 'fulfilled' ? unwrap(tri.value) : [],
        items: act.status === 'fulfilled' ? unwrap(act.value) : [],
      });
    })();
    return () => { live = false; };
  }, [visitId, patientId, user?.email, loadRx]);

  // The prescriptions that belong to THIS visit (draft or signed) vs. earlier signed ones.
  const rxList = useMemo(() => (allRx ? allRx.filter(r => r.visitId === visitId) : null), [allRx, visitId]);
  const previousRx = useMemo(
    () => (allRx || []).filter(r => r.status === 'SIGNED' && r.visitId !== visitId)
      .sort((a, b) => String(b.signedAt || b.createdAt || '').localeCompare(String(a.signedAt || a.createdAt || ''))),
    [allRx, visitId],
  );
  const rxInitial = rxList && (rxList.find(r => r.status === 'DRAFT') || rxList[0] || null);

  const loadHistory = useCallback(async () => {
    setHistory(h => ({ ...h, status: 'loading' }));
    try {
      const all = await listPatientVisits(patientId);
      setHistory({ status: 'ready', visits: all.filter(v => v.id !== visitId) });
    } catch { setHistory({ status: 'error', visits: [] }); }
  }, [patientId, visitId]);

  // ── Autosave ───────────────────────────────────────────────────────────────
  const saveBody = useCallback(async () => {
    if (!visitId || locked) return true;
    const payload = buildDraftPayload(formRef.current);
    const key = JSON.stringify(payload);
    if (key === savedKey.current) { setSaveState('saved'); return true; }
    setSaveState('saving');
    try {
      await updateVisit(visitId, payload);
      savedKey.current = key;
      setSavedAt(new Date());
      setSaveState(isDraftDirty(formRef.current, key) ? 'dirty' : 'saved');
      return true;
    } catch (err) {
      setSaveState('error');
      if (err?.response?.status === 409) toast.error('This visit was already signed elsewhere — reload the page');
      return false;
    }
  }, [visitId, locked, toast]);
  // One save at a time, in order. A save asked for while another is in flight (the autosave timer firing just as the
  // doctor presses Sign) waits for it and then re-reads the form, so it either finds nothing left to send or sends the
  // newest text — two PUTs can never race and leave an older diagnosis on the server. This is also what lets the Sign
  // button stay enabled while an autosave runs: disabling it for those ~100 ms swallowed a click that landed on it.
  const saveChain = useRef(Promise.resolve(true));
  const saveDraft = useCallback(() => {
    const next = saveChain.current.then(() => saveBody(), () => saveBody());
    saveChain.current = next;
    return next;
  }, [saveBody]);
  const saveRef = useRef(saveDraft);
  saveRef.current = saveDraft;
  const debouncer = useMemo(() => createDebouncer(() => saveRef.current(), AUTOSAVE_MS), []);

  useEffect(() => {
    if (!visitId || locked) return;
    if (isDraftDirty(form, savedKey.current)) { setSaveState(s => (s === 'saving' ? s : 'dirty')); debouncer.call(); }
  }, [form, visitId, locked, debouncer]);
  useEffect(() => () => { debouncer.flush(); }, [debouncer]);

  const setField = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), []);
  const setNextSteps = useCallback(updater => setForm(f => ({
    ...f, nextSteps: typeof updater === 'function' ? updater(f.nextSteps) : updater,
  })), []);

  // ── Prescription step ──────────────────────────────────────────────────────
  const medicineSummary = useMemo(
    () => rxItems.filter(i => i.drugName.trim()).map(i => describeItem(i)).join(' · '),
    [rxItems],
  );
  const setNoMedicines = useCallback(on => {
    setNoMedicinesState(on);
    if (visitId) writeNoMeds(visitId, on);
  }, [visitId]);
  // Adding a medicine supersedes "No medicines needed".
  useEffect(() => { if (rxItems.length > 0 && noMedicines) setNoMedicines(false); }, [rxItems.length, noMedicines, setNoMedicines]);

  /** Copy a previous prescription's medicines into the draft (see utils/copyMedicines.js). */
  const copyMedicines = useCallback(rx => {
    if (locked || !builderRef.current) return;
    const result = builderRef.current.addMedicines(rx);
    const msg = copyMedicinesMessage(result);
    if (result.added > 0) toast.success(msg); else toast.info(msg);
    return result;
  }, [locked, toast]);

  const reloadVitals = useCallback(() => {
    if (!patientId) return;
    getPatientVitals(patientId, 1).then(r => setVitals(r[0] || null)).catch(() => {});
  }, [patientId]);

  // ── Private notes ──────────────────────────────────────────────────────────
  const visitNotes = notes.filter(n => (n.content || '').startsWith(`${visitTag}:`));
  const saveNote = async () => {
    const text = noteDraft.trim();
    if (!text) return;
    setNoteSaving(true);
    try {
      const res = await doctorNoteAPI.create({ patientId, doctorEmail: user?.email, content: `${visitTag}: ${text}` });
      setNotes(prev => [res.data, ...prev]); setNoteDraft('');
      toast.success('Private note saved');
    } catch { toast.error("Couldn't save the note — please try again"); }
    finally { setNoteSaving(false); }
  };

  // ── Open action items ──────────────────────────────────────────────────────
  const toggleItem = useCallback(async item => {
    try {
      const res = await doctorActionItemAPI.update(item.id, { completed: !item.completed });
      setContext(c => ({
        ...c, items: c.items.map(i => (i.id === item.id ? (res.data || { ...i, completed: !i.completed }) : i)),
      }));
    } catch { toast.error("Couldn't update that item — please try again"); }
  }, [toast]);

  // ── Sign ───────────────────────────────────────────────────────────────────
  const diagnosisBlank = !canSign(form);
  // Not while a save is running: the click waits for it (the header button shows aria-disabled, but stays clickable so a
  // click that straddles the autosave starting is held, not lost) and then carries on with what was just saved.
  const savingRef = useRef(false);
  savingRef.current = saveState === 'saving';
  const askToSign = useCallback(async () => {
    if (savingRef.current) await saveChain.current;
    if (!canSign(formRef.current)) { toast.warning('Add a diagnosis before signing'); return; }
    if (builderRef.current?.hasUnsavedProblems()) { toast.warning('Fix the highlighted medicine rows (or remove them) before signing'); return; }
    setSignProblem(null); setConfirming(true);
  }, [toast]);

  const doSign = async () => {
    setSigning(true); setSignProblem(null);
    try {
      debouncer.cancel();
      if (!(await saveDraft())) { setSignProblem('Your latest edits could not be saved, so nothing was signed. Use Retry, then sign again.'); return; }
      const rxId = await builderRef.current?.ensureSaved();
      const drafts = (rxList || []).filter(r => r.status === 'DRAFT').map(r => r.id);
      const prescriptionIds = [...new Set([...drafts, rxId].filter(Boolean))];
      let signedVisit = null;
      const result = await signConsultation({
        prescriptionIds,
        signPrescription,
        signVisit: async () => { signedVisit = await signVisit(visitId); },
      });
      if (!result.ok) {
        setSignProblem(describeSignFailure(result));
        loadRx();
        return;
      }
      setVisit(v => ({ ...v, ...(signedVisit || {}), status: 'SIGNED' }));
      setConfirming(false);
      onStatusChange?.('COMPLETED');
      toast.success('Visit signed and completed');
      loadRx();
    } catch (err) {
      setSignProblem(err?.validation ? err.message : "Couldn't prepare the visit for signing — please try again.");
    } finally { setSigning(false); }
  };

  return {
    // lifecycle
    phase, loadError, openVisit, visit, visitId, locked, appointment,
    // form + autosave
    form, setField, setNextSteps, saveState, savedAt, saveDraft,
    // context
    vitals, reloadVitals, patient, queueEntry, allergies, context, toggleItem, history, loadHistory, notes, visitNotes,
    visitTag, noteDraft, setNoteDraft, noteSaving, saveNote,
    // prescriptions
    allRx, rxList, rxInitial, previousRx, builderRef, rxItems, setRxItems, medicineSummary,
    noMedicines, setNoMedicines, copyMedicines, loadRx,
    // sign
    diagnosisBlank, askToSign, confirming, setConfirming, signing, signProblem, doSign,
  };
}
